import { useCallback } from "react";
import { execCommandCopyFallback } from "../../../../integrations/tools/clipboard";
import { emitToast } from "../../../../integrations/tools/outlook/toast-bridge";
import { logOfficeError } from "../../../telemetry";

/**
 * Hands a mailbox query over to Outlook's own search (AIVA-7020, specification
 * MS-01 gap G-4) — the escape hatch for the fact that AiVA only ever searches
 * the first N matching messages.
 *
 * WHY THE CLIPBOARD, and not a link.
 *
 * The specification names the affordance and its purpose — "a 'Search in
 * Outlook' affordance that hands the query to native search" — but prescribes no
 * mechanism, and there is no good one:
 *
 *  - Office.js exposes no API for driving the host's native search box, in any
 *    Outlook client. There is displayMessageForm and displayAppointmentForm, and
 *    nothing for search.
 *  - Outlook Web has no search deep link. Verified against a live tenant on
 *    2026-08-17: running a search in OWA leaves the address bar completely
 *    unchanged, so search state is not URL-addressable and a `?q=` parameter is
 *    ignored. An earlier build of this hook navigated to
 *    `outlook.office.com/mail/search?q=…` and landed the user on their inbox
 *    with an empty search box — worse than useless, because it looked like it
 *    had worked.
 *
 * So the query is copied instead, and the toast says where to put it. One paste,
 * in whichever Outlook the user is actually in. That satisfies "hands the query
 * to native search" more honestly than a URL that silently drops it.
 *
 * The toast names no keyboard shortcut, deliberately. An earlier version said
 * "Press Ctrl+E" — which is the desktop shortcut (Outlook Web uses Alt+Q), and in
 * any case no shortcut reaches Outlook while focus is inside the add-in pane, so
 * the instruction read as broken. "Paste it into Outlook's search box" is true in
 * every client.
 *
 * The copy path mirrors the clipboard pill: the async API first, then
 * execCommandCopyFallback for hosts (Outlook desktop on Windows) that load the
 * add-in iframe without `clipboard-write` in their Permissions-Policy. A button
 * click is a real user gesture, so the legacy path is available to us.
 */
export function useOpenOutlookSearch(): (query: string) => void {
  return useCallback((query: string) => {
    const text = query.trim();
    if (!text) return;

    const confirm = (): void =>
      emitToast({ title: "Search copied. Paste it into Outlook's search box.", intent: "success" });
    const fail = (): void =>
      emitToast({ title: "Could not copy the search. Select the query and copy it.", intent: "error" });

    void (async () => {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text);
          confirm();
          return;
        }
      } catch (err) {
        // Blocked by Permissions-Policy rather than genuinely broken — fall
        // through to execCommand rather than reporting failure to the user.
        console.warn("[useOpenOutlookSearch] clipboard API blocked, falling back:", err);
        logOfficeError(err, "outlook-copy-search");
      }
      if (execCommandCopyFallback(text)) confirm();
      else fail();
    })();
  }, []);
}
